import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEvents } from '../../services/eventsService';

import { ReactComponent as Icon1 } from '../../assets/site-assets/home/event-1.svg';
import { ReactComponent as Icon2 } from '../../assets/site-assets/home/event-2.svg';
import { ReactComponent as Icon3 } from '../../assets/site-assets/home/event-3.svg';
import { ReactComponent as Icon4 } from '../../assets/site-assets/home/event-4.svg';
import { ReactComponent as Icon5 } from '../../assets/site-assets/home/event-5.svg';
import { ReactComponent as Icon6 } from '../../assets/site-assets/home/event-6.svg';
import { ReactComponent as Icon7 } from '../../assets/site-assets/home/event-7.svg';
import TreeMask from '../../assets/site-assets/home/tree-mask.png';
import { ReactComponent as SliderVector } from '../../assets/site-assets/home/slider-vector.svg';

import fallback1 from '../../assets/site-assets/home/swipe-vid-1.webp';
import fallback2 from '../../assets/site-assets/home/swipe-vid-2.webp';
import fallback3 from '../../assets/site-assets/home/swipe-vid-3.webp';
import fallback4 from '../../assets/site-assets/home/swipe-vid-4.webp';
import fallback5 from '../../assets/site-assets/home/swipe-vid-5.webp';

import './EventsSection.scss';

const fallbackImages = [fallback1, fallback2, fallback3, fallback4, fallback5];

const categoryIconDefs = [
  { Icon: Icon1, key: 'eventsSection.categories.happening' },
  { Icon: Icon2, key: 'eventsSection.categories.nature'    },
  { Icon: Icon3, key: 'eventsSection.categories.arts'      },
  { Icon: Icon4, key: 'eventsSection.categories.mosques'   },
  { Icon: Icon5, key: 'eventsSection.categories.design'    },
  { Icon: Icon6, key: 'eventsSection.categories.music'     },
  { Icon: Icon7, key: 'eventsSection.categories.community' },
];

const getCategoryVariant = (category) => {
  switch (category.toLowerCase()) {
    case 'all':                                                return 'orange';
    case 'unique experiences':                                 return 'orange';
    case 'outdoor & nature': case 'adventure':                 return 'olive';
    case 'culture & heritage': case 'sports':                  return 'dark';
    case 'art & design': case 'art':                           return 'yellow';
    case 'mosques': case 'family & entertainment': case 'family': return 'pink';
    default:                                                   return 'default';
  }
};

const EventsSection = () => {
  const { t, i18n }     = useTranslation();
  const [events,     setEvents]     = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [active,     setActive]     = useState('All');
  const [loading,    setLoading]    = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    setActive('All');

    fetchEvents(i18n.language, 8)
      .then(items => {
        if (!mounted.current) return;
        const withFallback = items.map((item, idx) => ({
          ...item,
          image: item.image || fallbackImages[idx % fallbackImages.length],
        }));
        setEvents(withFallback);
        const cats = [...new Set(items.map(ev => ev.category.trim()).filter(Boolean))];
        setCategories(['All', ...cats]);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted.current) return;
        setLoading(false);
      });

    return () => { mounted.current = false; };
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEvents = useMemo(() => {
    if (active === 'All') return events;
    const activeLower = active.trim().toLowerCase();
    return events.filter(ev => ev.category.trim().toLowerCase() === activeLower);
  }, [events, active]);

  return (
    <section className="events-section" style={{ backgroundImage: `url(${TreeMask})` }}>
      <div className="events-section__container">

        <div className="events-section__top-row">
          <div className="events-section__header">
            <h2 className="events-section__title">{t('eventsSection.title')}</h2>
            <p className="events-section__subtitle">
              {t('eventsSection.subtitlePrefix')}{' '}
              <span className="events-section__highlight">{t('eventsSection.subtitleHighlight')}</span>
            </p>
          </div>

          <div className="events-section__icons">
            {categoryIconDefs.map(({ Icon, key }) => (
              <button key={key} className="events-section__icon-btn" aria-label={t(key)}>
                <Icon />
              </button>
            ))}
          </div>
        </div>

        <div className="events-section__filters">
          {categories.map((category) => {
            const isActive = active === category;
            const variant  = getCategoryVariant(category);
            return (
              <button
                key={category}
                onClick={() => setActive(category)}
                className={[
                  'events-section__badge',
                  `events-section__badge--${variant}`,
                  isActive ? 'events-section__badge--selected' : '',
                ].join(' ').trim()}
              >
                {category}
              </button>
            );
          })}
        </div>

        <div className="events-section__grid">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="events-section__skeleton" aria-hidden="true" />
            ))
          ) : filteredEvents.length === 0 ? (
            <div className="events-section__empty">{t('eventsSection.empty')}</div>
          ) : (
            filteredEvents.map((ev, i) => (
              <a
                key={ev.link || i}
                href={ev.link}
                target="_blank"
                rel="noopener noreferrer"
                className="events-section__card"
                style={{ backgroundImage: `url(${ev.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                <div className="events-section__card-overlay" />
                <div className="events-section__card-body">
                  <h3 className="events-section__card-title">{ev.title}</h3>
                  <p className="events-section__card-meta">{ev.date}</p>
                  <p className="events-section__card-meta">{ev.location}</p>
                </div>
              </a>
            ))
          )}
        </div>

        <div className="events-section__cta-wrap">
          <a href={t('eventsSection.exploreAllUrl')} target="_blank" rel="noopener noreferrer" className="events-section__cta">
            {t('eventsSection.exploreAll')}
          </a>
        </div>

      </div>

      <div className="events-section__float" aria-hidden="true">
        <SliderVector />
      </div>
    </section>
  );
};

export default EventsSection;
